/**
 * Error display component for Plan Guide.
 *
 * Phase 5: Enhanced with accessibility and responsive styling.
 *
 * @see frontend/workspace/CU-868gzzjua/frontend_research.md - Q6 Error States
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button, Stack, Alert, AlertTitle } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

interface Props {
  error: { message: string; code?: number };
  onRetry?: () => void;
  onGoToHub?: () => void;
}

export const PlanGuideError: React.FC<Props> = ({
  error,
  onRetry,
  onGoToHub,
}) => {
  const showRetry = error.code === 500 || !error.code;
  const showGoToHub = error.code === 404;
  const alertRef = useRef<HTMLDivElement>(null);

  // Focus the error message for screen reader announcement
  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.focus();
    }
  }, [error]);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 500,
        mx: 'auto',
      }}
    >
      <Alert
        ref={alertRef}
        severity="error"
        tabIndex={-1}
        role="alert"
        aria-live="assertive"
        icon={<ErrorOutlineIcon fontSize="large" />}
        sx={{
          width: '100%',
          mb: 3,
          '& .MuiAlert-icon': {
            alignItems: 'center',
          },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>
          Something went wrong
        </AlertTitle>
        <Typography variant="body2">
          {error.message}
        </Typography>
        {error.code && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Error code: {error.code}
          </Typography>
        )}
      </Alert>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        {showRetry && onRetry && (
          <Button
            variant="contained"
            onClick={onRetry}
            startIcon={<RefreshIcon />}
            sx={{
              minHeight: { xs: 48, sm: 40 },
              minWidth: { xs: '100%', sm: 120 },
            }}
            aria-label="Retry loading the page"
          >
            Retry
          </Button>
        )}

        {showGoToHub && onGoToHub && (
          <Button
            variant="outlined"
            onClick={onGoToHub}
            startIcon={<HomeIcon />}
            sx={{
              minHeight: { xs: 48, sm: 40 },
              minWidth: { xs: '100%', sm: 150 },
            }}
            aria-label="Return to the Hub page"
          >
            Return to Hub
          </Button>
        )}
      </Stack>
    </Box>
  );
};
